import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { compact, every, isEmpty, pick, values } from 'lodash';
import { ERROR_CODES } from '@model/common/error-codes';
import { TripTypes, Page } from '@model/common';
import { TourHit } from '@model/search/alogolia/tour-hit';
import { performNavigate } from '@state/slices/app';
import { getSearchParams, getToursByType, setSearchParams, setTourByType } from '@state/slices/search/tours';
import { getLeadInTourReferences } from '@state/slices/tours/tour-references';
import { fetchTours } from '@state/slices/search-results/tours/toursSlice';
import {
  getToursError,
  getToursSearchResults,
  getToursSearchToken
} from '@state/slices/search-results/tours/toursSelectors';
import { getAlgoliaToursByType } from '@state/slices/algolia/algoliaSelectors';
import { performSearchTours } from '@state/slices/algolia/algoliaSlice';
import { parseOccupancy } from '@util/deal-finder';
import { toursSearchMapper } from '@util/tours';
import { dispatch } from '@state/store';
import { NO_AIRPORT_CODE } from '@model/iceberg/airports';

export const withToursParams = (WrappedComponent: any) => (props: any) => {
  const { query, path } = props;

  if (!query) {
    return <WrappedComponent {...props} />;
  }

  const { searchToken, tripType: tripTypeString, destinations, airports, month, adults, departureDate } = query;
  const tripType = tripTypeString as TripTypes;

  const [onloadComplete, setOnloadComplete] = useState(false);

  const router = useRouter();
  const routerPath = router.asPath;
  const tourReferences = useSelector(getLeadInTourReferences);
  const selectedTours = useSelector(getToursByType(tripType));
  const { data: algoliaTours } = useSelector(getAlgoliaToursByType(tripType));
  const searchParams = useSelector(getSearchParams(tripType));
  const toursResults = useSelector(getToursSearchResults);
  const toursError = useSelector(getToursError);
  const storedSearchToken = useSelector(getToursSearchToken);
  const nonDefaultSearchParams = pick(searchParams, ['airports', 'month']);

  const fetchSingleTour = async (tourPath) => {
    const { response } = (await performSearchTours({
      tripType: tripType,
      query: tourPath
    })) as any;

    if (response) {
      const { hits } = response;
      const tourMatch = hits.length === 1 ? hits[0] : hits.find(({ path }) => path.split('/').pop() === tourPath);
      if (tourMatch) {
        dispatch(
          setTourByType({
            tripType: tripType,
            tour: tourMatch
          })
        );
      }
    }
  };

  useEffect(() => {
    if (routerPath.startsWith(Page.TOURS_SEARCH)) {
      if (query && (every(values(nonDefaultSearchParams), isEmpty) || departureDate)) {
        const departureDateAsMonth = departureDate?.split('-').slice(0, 2).join('-');
        const validMonth = month ? month.split(',').map((m) => m.split('-').slice(0, 2).join('-')) : undefined;
        const validAirports = airports ? airports.split(',').filter((code) => code !== NO_AIRPORT_CODE) : undefined;
        dispatch(
          setSearchParams({
            setSearchType: true,
            airports: validAirports,
            month: validMonth ? validMonth : departureDateAsMonth ? [departureDateAsMonth] : undefined,
            occupancy: adults ? parseOccupancy({ adults, children: '' }) : undefined,
            tripType
          })
        );
      }
    }
  }, [routerPath]);

  useEffect(() => {
    if (!onloadComplete) {
      if (algoliaTours.length) {
        setOnloadComplete(true);

        if (destinations && tripType && !selectedTours.length) {
          const destinationsArray = destinations?.split(',');
          const destinationTours: Array<TourHit> = compact(
            destinationsArray?.map((path) => {
              const [continent, country, tourName] = path.split('/');
              if (tourName) {
                return;
              }
              const matchPath = `${continent}/${country}`;
              return algoliaTours.find((tour) => {
                return tour.path === matchPath;
              });
            })
          );

          // will populate tours for destination pages
          if (destinationTours && destinationTours.length === destinationsArray?.length) {
            destinationTours.forEach((tour) => {
              if (tour) {
                dispatch(
                  setTourByType({
                    tripType: tripType,
                    tour
                  })
                );
              }
            });
          } else {
            const [_a, _b, tourPath] = destinationsArray[0].split('/');
            if (tourPath) {
              fetchSingleTour(tourPath);
            }
          }
        }
      }
    }
  }, [algoliaTours]);

  useEffect(() => {
    if ((!tourReferences || !tourReferences.length || !toursResults.length) && !path) {
      const searchParams = searchToken ? { searchToken } : toursSearchMapper(query);
      dispatch(fetchTours(searchParams));
    }
  }, [searchToken, query]);

  useEffect(() => {
    if (toursError) {
      const { searchToken, ...validParams } = query;
      if (toursError === ERROR_CODES.TOKEN_EXPIRED) {
        const searchParams = toursSearchMapper(validParams);
        dispatch(fetchTours(searchParams));
      }
    }
  }, [toursError]);

  useEffect(() => {
    if (storedSearchToken && searchToken && storedSearchToken !== searchToken) {
      const { searchToken: querySearchToken, ...validParams } = query;
      const { pathname } = router;
      dispatch(
        performNavigate({ pathname, query: { searchToken: storedSearchToken, ...validParams } }, undefined, {
          shallow: true
        })
      );
    }
  }, [storedSearchToken, searchToken]);

  return <WrappedComponent {...props} />;
};
